import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Sparkles, ChevronRight, Loader2, CheckCircle, XCircle, UtensilsCrossed } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const durations = [
  { id: '2-hour', label: '2h', fullLabel: '2 Hours', stops: 2, description: 'Quick tasting tour' },
  { id: 'half-day', label: '4h', fullLabel: 'Half Day', stops: 4, description: 'Leisurely exploration' },
  { id: 'full-day', label: '6h', fullLabel: 'Full Day', stops: 7, description: 'Complete adventure' },
];

export default function RouteBuilder({ onBuildRoute, isLoading, selectedRegion, error, onClearHistory }) {
  const [selectedDuration, setSelectedDuration] = useState('half-day');

  const [includeDining, setIncludeDining] = useState(false);

  // User location state
  const [userLocation, setUserLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState('idle'); // idle, loading, success, error
  const [locationError, setLocationError] = useState(null);

  const selectedDurationInfo = durations.find(d => d.id === selectedDuration);

  // Get user's current location
  const getUserLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus('error');
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setLocationStatus('loading');
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setLocationStatus('success');
      },
      (error) => {
        setLocationStatus('error');
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location permission denied. Please enable location access.');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location information unavailable.');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out.');
            break;
          default:
            setLocationError('Unable to get your location.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 30000 // Cache for 30 seconds only — get a fresh GPS fix
      }
    );
  };

  const handleBuild = () => {
    onBuildRoute({
      duration: selectedDuration,
      region: selectedRegion,
      startLocation: userLocation,
      includeDining,
    });
  };

  return (
    <div className="space-y-6">
      {/* Duration Selection */}
      <p className="text-sm font-medium text-stone-600 mb-3">How much time do you have?</p>
      <div>
        <div className="grid grid-cols-3 gap-3">
          {durations.map((duration) => (
            <motion.button
              key={duration.id}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedDuration(duration.id)}
              className={cn(
                "relative p-4 rounded-xl border-2 transition-all",
                selectedDuration === duration.id
                  ? "border-[#722F37] bg-[#722F37]/5"
                  : "border-stone-200 bg-white hover:border-stone-300"
              )}
            >
              <div className="text-center">
                <div className={cn(
                  "text-2xl font-bold mb-1",
                  selectedDuration === duration.id ? "text-[#722F37]" : "text-stone-700"
                )}>
                  {duration.label}
                </div>
                <div className="text-xs text-stone-500">
                  {duration.stops} stops
                </div>
              </div>
              {selectedDuration === duration.id && (
                <motion.div
                  layoutId="duration-indicator"
                  className="absolute -top-1 -right-1 w-5 h-5 bg-[#C9A227] rounded-full flex items-center justify-center"
                >
                  <Sparkles className="w-3 h-3 text-white" />
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>
        <p className="text-sm text-stone-500 mt-2 text-center">
          {selectedDurationInfo?.description}
        </p>
      </div>

      {/* Dining Toggle */}
      <div className="flex items-center justify-between gap-3 p-4 rounded-xl border-2 border-stone-200">
        <div className="flex items-center gap-2.5">
          <UtensilsCrossed className="w-5 h-5 text-stone-500 shrink-0" />
          <div>
            <p className="text-sm font-medium text-stone-700">Add a midway dining stop at a winery</p>
            {includeDining && (
              <p className="text-xs text-stone-500 mt-0.5">One stop will include food service</p>
            )}
          </div>
        </div>
        <button
          onClick={() => setIncludeDining(prev => !prev)}
          className={cn(
            "relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0",
            includeDining ? "bg-[#722F37]" : "bg-stone-300"
          )}
        >
          <span
            className={cn(
              "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
              includeDining ? "translate-x-6" : "translate-x-1"
            )}
          />
        </button>
      </div>

      {/* Current Location */}
      <div>
        <p className="text-sm font-medium text-stone-600 mb-2">
          Where are you starting from?
        </p>
        <button
          onClick={getUserLocation}
          disabled={locationStatus === 'loading'}
          className={cn(
            "w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-colors",
            locationStatus === 'success'
              ? "border-green-500 bg-green-50 text-green-700"
              : locationStatus === 'error'
              ? "border-red-300 bg-red-50 text-red-600"
              : "border-[#722F37]/30 bg-[#722F37]/5 text-[#722F37] hover:border-[#722F37]/60 hover:bg-[#722F37]/10"
          )}
        >
          {locationStatus === 'loading' ? (
            <Loader2 className="w-5 h-5 animate-spin shrink-0" />
          ) : locationStatus === 'success' ? (
            <CheckCircle className="w-5 h-5 shrink-0" />
          ) : locationStatus === 'error' ? (
            <XCircle className="w-5 h-5 shrink-0" />
          ) : (
            <MapPin className="w-5 h-5 shrink-0" />
          )}
          <span className="text-sm text-left flex-1 font-medium">
            {locationStatus === 'loading' && 'Getting your location...'}
            {locationStatus === 'success' && 'Location detected — route optimized from your position'}
            {locationStatus === 'error' && (locationError || 'Location error')}
            {locationStatus === 'idle' && <>Tap to use <span className="font-semibold">my current location</span></>}
          </span>
        </button>
        {locationStatus === 'idle' && (
          <p className="text-xs text-stone-600 mt-1.5 ml-1">
            Recommended — this enables turn-by-turn directions
          </p>
        )}
        {locationStatus === 'error' && (
          <button
            onClick={getUserLocation}
            className="text-xs text-[#722F37] mt-2 hover:underline"
          >
            Try again
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm text-center space-y-1">
          <p>{error}</p>
          {onClearHistory && (
            <button
              onClick={onClearHistory}
              className="text-xs text-[#722F37] underline hover:no-underline"
            >
              Reset visit history &amp; try again
            </button>
          )}
        </div>
      )}

      {/* Build Button */}
      <Button
        onClick={handleBuild}
        disabled={isLoading}
        className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-[#722F37] to-[#8B3A42] hover:from-[#5a252c] hover:to-[#722F37] rounded-xl shadow-lg ring-2 ring-[#C9A227]/30"
      >
        {isLoading ? (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
          />
        ) : (
          <>
            Build My Wine Route
            <ChevronRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
      {/* Info */}
      <p className="-mt-5 text-xs text-center text-stone-600">
        We'll build a personalized tasting route with directions
      </p>
    </div>
  );
}
